import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Calendar, Camera, MessageCircle, X, Plus } from 'lucide-react';

export default function AddVisitModal({ open, onClose, spot, onAddVisit }) {
  const [visitData, setVisitData] = useState({
    date: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
    photos: [],
    description: '',
    comments: ''
  });
  const [loading, setLoading] = useState(false);
  const [dateInputSupported, setDateInputSupported] = useState(true);

  // Check if date input is supported
  useEffect(() => {
    const testInput = document.createElement('input');
    testInput.type = 'date';
    setDateInputSupported(testInput.type === 'date');
  }, []);

  // Reset form data when modal opens/closes or spot changes
  useEffect(() => {
    if (open && spot) {
      const today = new Date().toISOString().split('T')[0];
      setVisitData({
        date: today,
        photos: [],
        description: '',
        comments: ''
      });
    }
  }, [open, spot]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Map frontend data structure to backend expected format
      const visitPayload = {
        visit_date: new Date(visitData.date).toISOString(),
        photos: visitData.photos,
        description: visitData.description || null,
        rating: null, // Could be added later with star rating UI
        weather: null // Could be added later with weather selection UI
      };
      
      await onAddVisit(visitPayload);
      
      // Reset form
      setVisitData({
        date: new Date().toISOString().split('T')[0],
        photos: [],
        description: '',
        comments: ''
      });
      
      onClose();
    } catch (error) {
      console.error('Error adding visit:', error);
      alert('Erreur lors de l\'ajout de la visite: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoAdd = () => {
    // For now, just add a placeholder URL
    // In a real app, this would handle file upload
    const photoUrl = prompt('URL de la photo:');
    if (photoUrl) {
      setVisitData(prev => ({
        ...prev,
        photos: [...prev.photos, photoUrl]
      }));
    }
  };

  const removePhoto = (index) => {
    setVisitData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  if (!spot) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Ajouter une visite - {spot.title}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="date">Date de la visite</Label>
            {dateInputSupported ? (
              <div className="relative">
                <Input
                  id="date"
                  type="date"
                  value={visitData.date}
                  max={new Date().toISOString().split('T')[0]} // Can't select future dates
                  onChange={(e) => setVisitData(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full"
                  required
                />
                <Calendar 
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" 
                />
              </div>
            ) : (
              // Fallback for browsers that don't support date input
              <div className="grid grid-cols-3 gap-2">
                <select 
                  value={visitData.date.split('-')[2]} 
                  onChange={(e) => {
                    const [year, month] = visitData.date.split('-');
                    setVisitData(prev => ({ ...prev, date: `${year}-${month}-${e.target.value.padStart(2, '0')}` }));
                  }}
                  className="px-3 py-2 border rounded-md"
                  required
                >
                  {Array.from({length: 31}, (_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
                <select 
                  value={visitData.date.split('-')[1]} 
                  onChange={(e) => {
                    const [year, , day] = visitData.date.split('-');
                    setVisitData(prev => ({ ...prev, date: `${year}-${e.target.value.padStart(2, '0')}-${day}` }));
                  }}
                  className="px-3 py-2 border rounded-md"
                  required
                >
                  {['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'].map((month, i) => (
                    <option key={i + 1} value={i + 1}>{month}</option>
                  ))}
                </select>
                <select 
                  value={visitData.date.split('-')[0]} 
                  onChange={(e) => {
                    const [, month, day] = visitData.date.split('-');
                    setVisitData(prev => ({ ...prev, date: `${e.target.value}-${month}-${day}` }));
                  }}
                  className="px-3 py-2 border rounded-md"
                  required
                >
                  {Array.from({length: 10}, (_, i) => (
                    <option key={2025 - i} value={2025 - i}>{2025 - i}</option>
                  ))}
                </select>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {dateInputSupported 
                ? "Cliquez sur le champ pour ouvrir le calendrier" 
                : "Sélectionnez jour, mois et année"
              }
            </p>
          </div>

          <div>
            <Label htmlFor="description">Description de votre visite</Label>
            <Textarea
              id="description"
              value={visitData.description}
              onChange={(e) => setVisitData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Comment s'est passée votre visite ? Qu'avez-vous ressenti ?"
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="comments">Commentaires ou notes</Label>
            <Textarea
              id="comments"
              value={visitData.comments}
              onChange={(e) => setVisitData(prev => ({ ...prev, comments: e.target.value }))}
              placeholder="Des conseils pour les prochains visiteurs ? Conditions particulières ?"
              rows={2}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Photos de votre visite</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handlePhotoAdd}
              >
                <Plus className="h-4 w-4 mr-1" />
                Ajouter
              </Button>
            </div>
            
            {visitData.photos.length > 0 && (
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {visitData.photos.map((photo, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                    <Camera className="h-4 w-4 text-gray-500" />
                    <span className="text-sm flex-1 truncate">{photo}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removePhoto(index)}
                      className="p-1 h-6 w-6"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Ajout...' : 'Ajouter la visite'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}